import React from 'react';

import { PLATFORM_STATIC_ICONS_PNG } from '@/config/icons';
import { LomavisDateTimeFormatter1 } from '@/lomavisSharedUi/atoms/utils/datetime/LomavisDateTimeFormatter1';
import type { LomavisPlacementPlatform1 } from '@/lomavisSharedUi/molecules/segments/LomavisPlacementOption1';
import type {
  LomavisDataTableColumn1,
  LomavisDataTableRow1
} from '@/lomavisSharedUi/molecules/tables/LomavisDataTable1';
import {
  LomavisCampaignActionDropdown1,
  type LomavisCampaignActionDropdownItem1
} from './LomavisCampaignActionDropdown1';

export type LomavisCampaignReviewRowStatusKey1 =
  | 'draft'
  | 'publishing'
  | 'published'
  | 'failed';

export type LomavisCampaignReviewRow1 = LomavisDataTableRow1 & {
  campaign: string;
  objective: string;
  platforms: LomavisPlacementPlatform1[];
  start: string;
  end: string;
  spend: string;
  status: string;
  statusKey?: LomavisCampaignReviewRowStatusKey1;
  thumbnail: string;
  canDelete?: boolean;
  canEdit?: boolean;
  isDisabled?: boolean;
};

type TranslateFn = (
  key: string,
  fallback: string,
  options?: Record<string, string | number>
) => string;

const platformFallbackLabels: Record<LomavisPlacementPlatform1, string> = {
  facebook: 'Facebook',
  instagram: 'Instagram'
};

const renderCampaignPlatformIcons = (
  platforms: LomavisPlacementPlatform1[],
  t: TranslateFn
) => (
  <div className="d-inline-flex align-items-center justify-content-end gap-1">
    {platforms.map((platform) => (
      <img
        key={platform}
        src={PLATFORM_STATIC_ICONS_PNG[platform]}
        alt=""
        title={t(
          `adManager.platforms.${platform}`,
          platformFallbackLabels[platform]
        )}
        style={{
          height: 20,
          objectFit: 'contain',
          width: 20
        }}
      />
    ))}
  </div>
);

export const getCampaignReviewTableColumns = ({
  draftActionsDisabled,
  copyingDraftId,
  deletingDraftId,
  togglingDisabledDraftId,
  onDraftCopy,
  onDraftDelete,
  onDraftEdit,
  onDraftToggleDisabled,
  t = (_key, fallback) => fallback
}: {
  draftActionsDisabled?: boolean;
  copyingDraftId?: string | null;
  deletingDraftId?: string | null;
  togglingDisabledDraftId?: string | null;
  onDraftCopy?: (id: string) => void;
  onDraftDelete?: (id: string) => void;
  onDraftEdit?: (id: string) => void;
  onDraftToggleDisabled?: (id: string, nextDisabled: boolean) => void;
  t?: TranslateFn;
}): LomavisDataTableColumn1[] => [
  {
    key: 'campaign',
    header: t('adManager.table.campaign', 'Campaign'),
    render: (row) => {
      const campaign = row as LomavisCampaignReviewRow1;
      const isPublished = campaign.statusKey === 'published';
      const isDisabled = campaign.isDisabled ?? false;

      return (
        <div className="d-flex align-items-center gap-3">
          <span
            className="d-inline-flex flex-shrink-0 rounded-3 position-relative"
            style={{
              background: campaign.thumbnail,
              height: 46,
              width: 46
            }}
          >
            {isPublished ? (
              <span
                aria-hidden="true"
                title={
                  isDisabled
                    ? t('adManager.status.disabled', 'Disabled')
                    : t('adManager.status.enabled', 'Enabled')
                }
                style={{
                  position: 'absolute',
                  bottom: -2,
                  right: -2,
                  height: 14,
                  width: 14,
                  borderRadius: '50%',
                  border: '2px solid #fff',
                  backgroundColor: isDisabled ? '#9aa0a6' : '#22c55e'
                }}
              />
            ) : null}
          </span>
          <span className="d-grid gap-1" style={{ minWidth: 0 }}>
            <span className="text-truncate fw-bold text-body">
              {campaign.campaign}
            </span>
          </span>
        </div>
      );
    },
    width: 360
  },
  {
    key: 'status',
    header: t('adManager.table.status', 'Status'),
    align: 'end',
    render: (row) => {
      const campaign = row as LomavisCampaignReviewRow1;
      const badgeClassByStatus: Record<
        LomavisCampaignReviewRowStatusKey1,
        string
      > = {
        draft: 'text-bg-warning',
        publishing: 'text-bg-info',
        published: 'text-bg-success',
        failed: 'text-bg-danger'
      };
      const badgeClass = campaign.statusKey
        ? badgeClassByStatus[campaign.statusKey]
        : 'text-bg-light';

      return (
        <span
          className={`badge rounded-pill text-uppercase fw-bold ${badgeClass}`}
        >
          <span className="me-1" aria-hidden="true">
            ·
          </span>
          {campaign.status}
        </span>
      );
    },
    width: 100,
    nowrap: true
  },
  {
    key: 'objective',
    header: t('adManager.table.objective', 'Objective'),
    align: 'end',
    width: 130,
    nowrap: true
  },
  {
    key: 'platforms',
    header: t('adManager.table.platforms', 'Platforms'),
    align: 'end',
    render: (row) =>
      renderCampaignPlatformIcons(
        (row as LomavisCampaignReviewRow1).platforms,
        t
      ),
    width: 105,
    nowrap: true
  },
  {
    key: 'start',
    header: t('adManager.table.start', 'Start'),
    align: 'end',
    render: (row) => {
      const { start } = row as LomavisCampaignReviewRow1;
      if (!start || start === '-') return start || '-';
      return <LomavisDateTimeFormatter1 dateTimeStr={start} dateOnly />;
    },
    width: 100,
    nowrap: true
  },
  {
    key: 'end',
    header: t('adManager.table.end', 'End'),
    align: 'end',
    render: (row) => {
      const { end } = row as LomavisCampaignReviewRow1;
      if (!end || end === '-') return end || '-';
      return <LomavisDateTimeFormatter1 dateTimeStr={end} dateOnly />;
    },
    width: 100,
    nowrap: true
  },
  {
    key: 'spend',
    header: t('adManager.table.spend', 'Spend'),
    align: 'end',
    numeric: true,
    width: 110,
    nowrap: true
  },
  {
    key: 'enabled',
    header: t('adManager.table.enabled', 'Enabled'),
    align: 'end',
    render: (row) => {
      const campaign = row as LomavisCampaignReviewRow1;
      // Toggling live campaigns pauses/activates them on Meta, so it only
      // makes sense once the campaign is actually live on Facebook.
      if (campaign.statusKey !== 'published') return null;

      const draftId = String(campaign.id ?? '');
      const isDisabled = campaign.isDisabled ?? false;
      const isBusy = togglingDisabledDraftId === draftId;
      const canToggle = Boolean(onDraftToggleDisabled && draftId);
      const disabledInput =
        !canToggle || draftActionsDisabled || isBusy;

      return (
        <div
          className="form-check form-switch d-inline-flex m-0"
          onClick={(event) => event.stopPropagation()}
        >
          <input
            className="form-check-input"
            type="checkbox"
            role="switch"
            checked={!isDisabled}
            disabled={disabledInput}
            onChange={() => {
              if (!draftId) return;
              onDraftToggleDisabled?.(draftId, !isDisabled);
            }}
            aria-label={
              isDisabled
                ? t('adManager.actions.enable', 'Enable')
                : t('adManager.actions.disable', 'Disable')
            }
          />
        </div>
      );
    },
    width: 90,
    nowrap: true
  },
  {
    key: 'actions',
    header: t('adManager.table.action', 'Action'),
    align: 'end',
    render: (row) => {
      const campaign = row as LomavisCampaignReviewRow1;
      const draftId = String(campaign.id ?? '');
      const canDelete = campaign.canDelete ?? true;
      const canEdit = campaign.canEdit ?? true;
      const isCopying = copyingDraftId === draftId;
      const isDeleting = deletingDraftId === draftId;

      const items: LomavisCampaignActionDropdownItem1[] = [
        {
          key: 'copy',
          label: isCopying
            ? t('adManager.actions.copying', 'Copying...')
            : t('adManager.actions.copy', 'Copy'),
          disabled:
            draftActionsDisabled || !onDraftCopy || !draftId || isCopying,
          onClick: () => {
            if (draftId) onDraftCopy?.(draftId);
          }
        },
        {
          key: 'delete',
          label: isDeleting
            ? t('adManager.actions.deleting', 'Deleting...')
            : t('adManager.actions.delete', 'Delete'),
          disabled:
            draftActionsDisabled ||
            !onDraftDelete ||
            !draftId ||
            isDeleting ||
            !canDelete,
          onClick: () => {
            if (draftId) onDraftDelete?.(draftId);
          }
        }
      ];

      return (
        <LomavisCampaignActionDropdown1
          items={items}
          isBusy={isCopying || isDeleting}
          drop="down"
          primaryAction={{
            label: t('adManager.actions.open', 'Open'),
            disabled:
              draftActionsDisabled || !onDraftEdit || !draftId || !canEdit,
            onClick: () => {
              if (draftId) onDraftEdit?.(draftId);
            }
          }}
          ariaLabel={t(
            'adManager.actions.rowActions',
            'Actions for {campaign}',
            { campaign: campaign.campaign }
          )}
        />
      );
    },
    width: 140,
    nowrap: true
  }
];
